import React, { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import ChronicPain from "./pages/ChronicPain";
import ConditionPage from "./pages/ConditionPage";
import AboutUs from "./pages/AboutUs";
import FAQPage from "./pages/FAQPage";
import TransferClinic from "./pages/TransferClinic";
import HowItWorksPage from "./pages/HowItWorksPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ZeroTolerance from "./pages/ZeroTolerance";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import Terms from "./pages/Terms";
import Complaints from "./pages/Complaints";
import SitemapPage from "./pages/SitemapPage";
import NotFound from "./pages/NotFound";
import PatientRejoiningPolicy from "./pages/PatientRejoiningPolicy";
import { Toaster } from "./components/ui/sonner";

/**
 * RouteTracker — manages two head-tag concerns on every React Router navigation:
 *
 * 1. Canonical link tag
 *    Sets <link rel="canonical" href="https://integroclinics.com{pathname}"> so
 *    Google uses the non-www domain as the authoritative URL for every page.
 *    location.pathname never contains query strings, so canonicals are always clean.
 *
 * 2. GA4 page_view
 *    The initial page view is sent automatically by the gtag('config', ...) call
 *    in public/index.html, so the very first render is skipped here to avoid
 *    double-counting that hit. Every subsequent SPA route change is tracked.
 *
 * Must be rendered inside <BrowserRouter> so useLocation() is available.
 */
function RouteTracker() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // ── Canonical link tag ─────────────────────────────────────────────────
    const canonicalHref = `https://integroclinics.com${location.pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonicalHref;

    // ── GA4 page_view ──────────────────────────────────────────────────────
    // Skip first render — initial page_view already sent by gtag config call.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    document.title = "Integro Clinics | Medical Cannabis Clinic UK";
  }, []);

  return (
    <div className="App relative">
      <BrowserRouter basename={process.env.REACT_APP_BASENAME || "/"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/chronic-pain" element={<ChronicPain />} />
          <Route path="/conditions/:conditionId" element={<ConditionPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/transfer-clinic" element={<TransferClinic />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/zero-tolerance-policy" element={<ZeroTolerance />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/complaints" element={<Complaints />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/patient-rejoining-policy" element={<PatientRejoiningPolicy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <RouteTracker />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
